"use client";

import { useMemo, useState } from "react";
import { currentIndiaDate, formatVedicEngineDate } from "@/lib/vedic-engine-date";

/**
 * Engine-backed Hindi vrat date table (rule 23/24). Generic over the shared
 * FestivalSummary shape (ekadashi / amavasya / purnima / pradosh / sankashti),
 * so every Hindi vrat twin renders the SAME date component — no per-vrat copy to
 * drift. EVERY date here is the value the backend astrology engine returned for
 * that tithi; no date is hand-typed in this component or on any page. The "अगली"
 * (next) highlight is derived by comparing engine dates to today's IST date.
 */
export interface HiVratEntry {
  id: string;
  name_en: string;
  name_hi: string;
  date: string;
  month_hi: string;
}

export interface HiVratYearData {
  year: number;
  entries: HiVratEntry[];
}

export default function HiVratDates({
  years,
  vratLabelHi,
}: {
  years: HiVratYearData[];
  vratLabelHi: string;
}) {
  const available = years.filter((y) => y.entries.length > 0);
  const [selected, setSelected] = useState<number>(available[0]?.year ?? years[0]?.year ?? 2026);

  const today = currentIndiaDate();
  const active = useMemo(
    () => years.find((y) => y.year === selected) ?? years[0],
    [years, selected]
  );

  const nextDate = useMemo(() => {
    const allDates = years
      .flatMap((y) => y.entries.map((e) => e.date))
      .filter((d) => d >= today)
      .sort();
    return allDates[0] ?? null;
  }, [years, today]);

  if (available.length === 0) {
    return (
      <p className="text-[15px] leading-[1.75]" style={{ color: "var(--ish-text-muted)" }}>
        {vratLabelHi} की तिथियाँ वैदिक इंजन से तुरंत गणना की जाती हैं। अभी सूची लोड नहीं हो सकी —
        कृपया एक क्षण में पुनः लोड करें ताकि हर तिथि अपने हिंदू मास के साथ दिखे।
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label={`${vratLabelHi} वर्ष`}>
        {years.map((y) => {
          const isActive = y.year === selected;
          const isEmpty = y.entries.length === 0;
          return (
            <button
              key={y.year}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={isEmpty}
              onClick={() => setSelected(y.year)}
              className="px-4 py-1.5 rounded-lg text-[13px] font-semibold transition disabled:opacity-40"
              style={{
                backgroundColor: isActive ? "var(--ish-accent)" : "var(--ish-accent-light)",
                color: isActive ? "#fff" : "var(--ish-accent)",
                border: "1px solid var(--ish-accent-border)",
              }}
            >
              {vratLabelHi} {y.year}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid var(--ish-border)" }}>
        <table className="w-full border-collapse text-left text-[13px]">
          <thead>
            <tr style={{ backgroundColor: "var(--ish-accent-light)" }}>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                {vratLabelHi}
              </th>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                तिथि
              </th>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                हिंदू मास
              </th>
            </tr>
          </thead>
          <tbody>
            {active.entries.map((e) => {
              const isNext = e.date === nextDate;
              return (
                <tr
                  key={e.id}
                  style={{
                    borderTop: "1px solid var(--ish-border-light)",
                    backgroundColor: isNext ? "var(--ish-accent-light)" : undefined,
                  }}
                >
                  <td className="px-3 py-2.5">
                    <span className="font-semibold" style={{ color: "var(--ish-dark)" }}>
                      {e.name_hi || e.name_en}
                    </span>
                    {isNext ? (
                      <span
                        className="ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: "var(--ish-accent)", color: "#fff" }}
                      >
                        अगली
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ish-text)" }}>
                    {formatVedicEngineDate(e.date, "hi")}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ish-text-muted)" }}>
                    {e.month_hi || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
        सभी तिथियाँ Swiss Ephemeris की वास्तविक ग्रह गणना (लाहिरी अयनांश) से निकाली गई हैं — तिथि की
        वास्तविक संधि, कोई निश्चित कैलेंडर नहीं।
      </p>
    </div>
  );
}
